import React, { useEffect, useState } from "react";
import { X, Wand2 } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import {
  generateQuestions,
  createAssignmentFromAI,
} from "../../../../services/program.service";

// Phase 3d — AI Quiz publisher. Two-step flow under one click:
//   1. Call POST /api/ai/generate-questions  (Python, with mock fallback)
//   2. Call POST /api/assignment/courses/:id/assignments/from-ai (Node)
//      which resolves each question's CO code → CourseOutcomeV2._id and
//      saves an `isActive: false` draft Assignment for teacher review.

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const BLOOM_OPTIONS = [
  { value: "", label: "Mixed (default)" },
  { value: "remember", label: "Remember" },
  { value: "understand", label: "Understand" },
  { value: "apply", label: "Apply" },
  { value: "analyze", label: "Analyze" },
  { value: "evaluate", label: "Evaluate" },
  { value: "create", label: "Create" },
];

const AIQuizForm = ({ courseId, courseCode, outcomes = [], onClose }) => {
  const [title, setTitle] = useState(`AI Quiz – ${courseCode || "Course"}`);
  const [numQuestions, setNumQuestions] = useState(5);
  const [type, setType] = useState("subjective");
  const [selectedCoCodes, setSelectedCoCodes] = useState([]);
  const [bloom, setBloom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Default all COs selected when modal opens
  useEffect(() => {
    if (outcomes.length > 0) {
      setSelectedCoCodes(outcomes.map((o) => o.code));
    }
  }, [outcomes]);

  const toggleCo = (code) => {
    setSelectedCoCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (selectedCoCodes.length === 0) {
      setError("Select at least one CO to target.");
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: AI generation
      const generated = await generateQuestions({
        course_code: courseCode,
        selected_cos: selectedCoCodes,
        selected_type: type,
        selected_bloom: bloom || "Mixed",
        num_questions: Number(numQuestions),
      });
      const questions = generated.questions || [];
      if (questions.length === 0) {
        setError("AI returned no questions. Try again.");
        setSubmitting(false);
        return;
      }
      const isMock = !!generated._mock;

      // Step 2: Publish to draft assignment
      const result = await createAssignmentFromAI(courseId, {
        title: title.trim(),
        description: `AI-generated quiz targeting ${selectedCoCodes.join(", ")}.`,
        instructions:
          "Review and edit each question, set the due date, then activate to publish to students.",
        totalPoints: questions.length * 10,
        questions,
      });

      toast.success(
        `Draft assignment created with ${result.assignment.questions.length} question(s)` +
          (isMock ? " (demo data — start Python AI for real)" : "")
      );
      if (result.warnings && result.warnings.length > 0) {
        result.warnings.forEach((w) =>
          toast(w, { icon: "⚠️", duration: 6000 })
        );
      }
      onClose(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Quiz generation failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => !submitting && onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>AI Quiz</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                Generate a quiz from your COs
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                AI drafts the questions tagged to the COs and Bloom level you
                pick. The result lands as a draft Assignment — review and
                publish from the Assignments tab.
              </p>
            </div>
            <button
              onClick={() => onClose(false)}
              disabled={submitting}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <Field label="Title *" htmlFor="title">
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className={inputBase}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Type" htmlFor="type">
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={inputBase}
                >
                  <option value="subjective">Subjective</option>
                  <option value="objective">Objective (MCQ)</option>
                </select>
              </Field>
              <Field label="Question count" htmlFor="num">
                <input
                  id="num"
                  type="number"
                  min={1}
                  max={20}
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(e.target.value)}
                  className={inputBase}
                />
              </Field>
              <Field label="Bloom level" htmlFor="bloom">
                <select
                  id="bloom"
                  value={bloom}
                  onChange={(e) => setBloom(e.target.value)}
                  className={inputBase}
                >
                  {BLOOM_OPTIONS.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                Target COs *
              </label>
              {outcomes.length === 0 ? (
                <p className="text-sm text-amber-600 dark:text-amber-400 italic">
                  No COs defined for this course yet. Add some in the section
                  above before generating a quiz.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {outcomes.map((o) => {
                    const checked = selectedCoCodes.includes(o.code);
                    return (
                      <label
                        key={o._id}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors",
                          checked
                            ? "border-accent1 bg-accent2"
                            : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCo(o.code)}
                          className="accent-current"
                        />
                        <span
                          className={cn(
                            "text-xs font-bold",
                            checked ? "text-accent1" : "text-gray-700 dark:text-gray-200"
                          )}
                        >
                          {o.code}
                        </span>
                        {o.bloomLevel && (
                          <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                            {o.bloomLevel}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || outcomes.length === 0}
                className={cn(
                  "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting || outcomes.length === 0
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                <Wand2 className="h-4 w-4" />
                {submitting ? "Generating…" : "Generate quiz"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIQuizForm;
